import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AlertCircle, Save } from 'react-feather';
import SweetAlert from 'sweetalert2';

import config from '../../../../config';
import ModalShell from '../../Settings/shared/ModalShell';

const CAPTURE_METHODS = ['API', 'DB', 'CSV', 'PDF', 'Push', 'Manual'];
const TRANSFER_MODES = ['API', 'Mail', 'FTP/SFTP', 'DB', 'Manual'];

/**
 * State-machine modal for tenant status changes. Same transitions as
 * the legacy /admin/tenant/tenantstatuschange/:id page:
 *
 *   tenant_disable === true       → only "Reactivate"
 *   tenant_status null / empty    → only "UAT"
 *   tenant_status === 'UAT'       → only "Live"
 *   tenant_status === 'Live'      → only "Disable"
 *
 * Wire contract preserved verbatim — payload posted to
 *   POST /tenant/updatetenant/:id      (status fields + disable flag)
 *   POST /tenantstatus                  (audit log row)
 * matches what StatusChange.jsx wrote, so backend is untouched.
 */
const TenantStatusModal = ({ tenantId, onClose, onSaved }) => {
    const { t } = useTranslation();
    const concessionaireId = localStorage.getItem('concessionaireId');
    const loginName = localStorage.getItem('loginName');
    const loginId = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [tenantName, setTenantName] = useState('');
    const [currentStatus, setCurrentStatus] = useState('');
    const [currentDisable, setCurrentDisable] = useState(false);
    const [loading, setLoading] = useState(true);

    const [nextStatus, setNextStatus] = useState('');
    const [date, setDate] = useState('');
    const [captureMethod, setCaptureMethod] = useState('');
    const [transferMode, setTransferMode] = useState('');
    const [reason, setReason] = useState('');
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const today = new Date().toISOString().split('T')[0];

    useEffect(() => {
        if (!tenantId) return;
        let cancelled = false;
        setLoading(true);
        axios.get(`${config.hostname}/tenant/${tenantId}`, { headers })
            .then((res) => {
                if (cancelled) return;
                setTenantName(res.data?.tenant_store_name || '');
                setCurrentStatus(res.data?.tenant_status || '');
                setCurrentDisable(!!res.data?.tenant_disable);
            })
            .catch((err) => {
                console.error('status modal load failed', err);
                toast.error(t('Failed to load tenant'));
                onClose?.();
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [tenantId, headers, onClose]);

    // Compute the single allowed transition for the current state.
    const allowedNext = useMemo(() => {
        if (currentDisable) return 'Reactivate';
        if (!currentStatus || currentStatus === 'null') return 'UAT';
        if (currentStatus === 'UAT') return 'Live';
        if (currentStatus === 'Live') return 'Disable';
        return null;
    }, [currentDisable, currentStatus]);

    // Default the select to the allowed transition once loaded.
    useEffect(() => {
        if (allowedNext) setNextStatus(allowedNext);
    }, [allowedNext]);

    const showCaptureFields = currentStatus === 'UAT' && nextStatus === 'Live';
    // The V2 page used `tenant_disableInitial === true || tenant_statusInitial === 'Live'`
    // which due to JS && precedence evaluated as `disable || (Live && live=>Disable)`.
    // For the modal we follow the *intent*: ask for a reason on Disable and on Reactivate.
    const showReason = nextStatus === 'Disable' || nextStatus === 'Reactivate';

    const validate = () => {
        const next = {};
        if (!nextStatus) next.nextStatus = t('Pick a status');
        if (!date) next.date = t('Date is required');
        if (showCaptureFields) {
            if (!captureMethod) next.captureMethod = t('Required');
            if (!transferMode) next.transferMode = t('Required');
        }
        if (showReason && !reason.trim()) next.reason = t('Reason is required');
        setErrors(next);
        return next;
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        const errs = validate();
        if (Object.keys(errs).length) return;

        const alertMsg = t('Mark {{name}} as {{status}}?', { name: tenantName, status: nextStatus });
        const result = await SweetAlert.fire({
            title: alertMsg,
            showCancelButton: true,
            cancelButtonText: t('No'),
            confirmButtonText: t('Yes'),
            reverseButtons: true,
        });
        if (!result.isConfirmed) return;

        // Build payload matching the legacy V2 contract.
        const payload = { login_name: loginName, login_id: loginId };
        let task = '';

        if (nextStatus === 'UAT') {
            payload.tenant_status = 'UAT';
            payload.tenant_uat_date = date;
            payload.tenant_uat_assignee = loginName;
            payload.tenant_disable = false;
            task = `${tenantName} Changed to UAT`;
        } else if (nextStatus === 'Live') {
            payload.tenant_status = 'Live';
            payload.tenant_live_date = date;
            payload.tenant_live_assignee = loginName;
            payload.tenant_disable = false;
            if (showCaptureFields) {
                payload.tenant_data_capture_method = captureMethod;
                payload.tenant_mode_of_transfer = transferMode;
            }
            task = `${tenantName} Changed to Live`;
        } else if (nextStatus === 'Disable') {
            payload.tenant_disable = true;
            payload.tenant_disable_date = date;
            payload.tenant_disable_assignee = loginName;
            payload.tenant_disable_reason = reason;
            task = `${tenantName} Disabled`;
        } else if (nextStatus === 'Reactivate') {
            payload.tenant_disable = false;
            payload.tenant_reactivate_date = date;
            payload.tenant_reactivate_assignee = loginName;
            payload.tenant_reactivate_reason = reason;
            task = `${tenantName} Reactivated`;
        }
        payload.task = task;

        const logData = {
            tenant_concessionaire_id: concessionaireId,
            tenant_id: tenantId,
            task,
            date,
            modified_by: loginName,
        };
        if (payload.tenant_disable_reason) logData.reason = payload.tenant_disable_reason;
        else if (payload.tenant_reactivate_reason) logData.reason = payload.tenant_reactivate_reason;

        setSaving(true);
        try {
            await axios.post(`${config.hostname}/tenant/updatetenant/${tenantId}`, payload, { headers });
            // Audit log insert; failure here shouldn't block the save toast.
            axios.post(`${config.hostname}/tenantstatus`, logData, { headers }).catch((err) => {
                console.error('tenantstatus log failed', err);
            });
            toast.success(t('{{name}} marked {{status}}', { name: tenantName, status: nextStatus }));
            onSaved?.();
            onClose?.();
        } catch (err) {
            console.error('status update failed', err);
            toast.error(t('Failed to update status'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalShell
            title={tenantName ? t('Change status — {{name}}', { name: tenantName }) : t('Change status')}
            onClose={onClose}
            maxWidth={520}
        >
            {loading ? (
                <div className="v3-sadmin-empty"><p>{t('Loading…')}</p></div>
            ) : !allowedNext ? (
                <div className="v3-sadmin-empty">
                    <p>{t('This tenant has no further status transitions available.')}</p>
                </div>
            ) : (
                <form className="v3-sadmin-form" onSubmit={handleSubmit}>
                    <div className="v3-sadmin-form-grid">
                        <label className="v3-field">
                            <span className="v3-label">{t('New status')}</span>
                            <div className="v3-select-wrap">
                                <select
                                    className="v3-input v3-select"
                                    value={nextStatus}
                                    onChange={(e) => setNextStatus(e.target.value)}
                                >
                                    <option value="">{t('Select…')}</option>
                                    <option value={allowedNext}>{t(allowedNext)}</option>
                                </select>
                            </div>
                            {errors.nextStatus && (
                                <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.nextStatus}</span>
                            )}
                        </label>
                        <label className="v3-field">
                            <span className="v3-label">{t('Effective date')}</span>
                            <input
                                className="v3-input"
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                max={today}
                            />
                            {errors.date && (
                                <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.date}</span>
                            )}
                        </label>

                        {showCaptureFields && (
                            <>
                                <label className="v3-field">
                                    <span className="v3-label">{t('Data capture method')}</span>
                                    <div className="v3-select-wrap">
                                        <select
                                            className="v3-input v3-select"
                                            value={captureMethod}
                                            onChange={(e) => setCaptureMethod(e.target.value)}
                                        >
                                            <option value="">{t('Select…')}</option>
                                            {CAPTURE_METHODS.map((m) => (
                                                <option key={m} value={m}>{t(m)}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.captureMethod && (
                                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.captureMethod}</span>
                                    )}
                                </label>
                                <label className="v3-field">
                                    <span className="v3-label">{t('Mode of transfer')}</span>
                                    <div className="v3-select-wrap">
                                        <select
                                            className="v3-input v3-select"
                                            value={transferMode}
                                            onChange={(e) => setTransferMode(e.target.value)}
                                        >
                                            <option value="">{t('Select…')}</option>
                                            {TRANSFER_MODES.map((m) => (
                                                <option key={m} value={m}>{t(m)}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.transferMode && (
                                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.transferMode}</span>
                                    )}
                                </label>
                            </>
                        )}
                    </div>

                    {showReason && (
                        <label className="v3-field">
                            <span className="v3-label">{t('Reason')}</span>
                            <textarea
                                className="v3-input"
                                rows={3}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder={nextStatus === 'Disable' ? t('Why is this tenant being disabled?') : t('Why is this tenant being reactivated?')}
                            />
                            {errors.reason && (
                                <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.reason}</span>
                            )}
                        </label>
                    )}

                    <div className="v3-sadmin-form-actions">
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={onClose} disabled={saving}>
                            {t('Cancel')}
                        </button>
                        <button type="submit" className="v3-btn v3-btn-primary" disabled={saving}>
                            <Save size={14} /> {saving ? t('Saving…') : nextStatus ? t('Mark {{status}}', { status: nextStatus }) : t('Mark status')}
                        </button>
                    </div>
                </form>
            )}
        </ModalShell>
    );
};

export default TenantStatusModal;
